// EXEMPT

// This file is for actions to update state, which are based on
// user input and not data fetching.

import { ref } from "valtio";

import type { DigAppState, VolatileState } from "@/__main__/app-state.mjs";
import {
  // This is fine because this file is for actions.
  __ONLY_WRITE_STATE_FROM_ACTIONS as writeState,
  readState,
} from "@/__main__/app-state.mjs";
import {
  IS_APP,
  IS_MAC,
  IS_NODE,
  isPersistent,
  isVolatile,
  MAX_TIME,
} from "@/__main__/constants.mjs";
import type { FeatureFlags } from "@/__main__/feature-flags.mjs";
import {
  allowRead,
  featurePromises,
  waitForFeatureFlags,
} from "@/__main__/feature-flags.mjs";
import { readData } from "@/__main__/get-data.mjs";
import initialState, { freshInitialState } from "@/__main__/initial-state.mjs";
import blitzMessage, { EVENTS, handleMessage } from "@/__main__/ipc-core.mjs";
import mainRefs from "@/__main__/refs.mjs";
import router, { EVENT_CHANGE_ROUTE, updateRoute } from "@/__main__/router.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import {
  ACTIVE_GAMES,
  EVENT_FEATURE_VERSION,
  GAME_SHORT_NAMES,
  GAME_SYMBOL_BY_SHORT_NAME,
} from "@/app/constants.mjs";
import type { GameSymbol } from "@/app/games.mjs";
import appRefs from "@/app/refs.mjs";
import type {
  SnackbarDismissedByRouting,
  SnackbarMessage,
  SnackbarMessageInit,
} from "@/app/Snackbar.js";
import { GAME_SYMBOL_LOL } from "@/game-lol/definition-symbol.mjs";
import lolRefs from "@/game-lol/refs.mjs";
import { GAME_SYMBOL_TFT } from "@/game-tft/definition-symbol.mjs";
import { initOverlaySettings } from "@/library/actions.mjs";
import clone from "@/util/clone.mjs";
import deepEqual from "@/util/deep-equal.mjs";
import { devDebug, devError, devLog, devWarn } from "@/util/dev.mjs";
import digPath from "@/util/dig-path.mjs";
import gameShortNameReassign from "@/util/game-short-name-reassign.mjs";
import lazyFn from "@/util/lazy-fn.mjs";
import optionalMerge from "@/util/optional-merge.mjs";
import {
  getABFeatureValue,
  getEnvironmentIdentifier,
  getFeatureIdentifier,
} from "@/util/use-ab-feature.mjs";

const fetchFeatureFlags = lazyFn(() => import("@/app/fetch-feature-flags.mjs"));

let initSettingsPromise: Promise<void>;

/**
 * Ensure settings are loaded. This will only resolve once the app has rendered at least once.
 */
export function initSettings() {
  if (IS_NODE) return;

  if (!initSettingsPromise)
    initSettingsPromise = (async () => {
      const settings = clone(
        (await readData(["settings"])) || freshInitialState.settings,
      );

      optionalMerge(settings, initialState.settings);
      initOverlaySettings(settings);
      await assignFavoriteGames(settings);

      const updatedLastLoggedInIdByGame = migrateLinkedAccounts(
        settings.lastLoggedInIdByGame,
      );
      const updatedLoggedInAccounts = migrateLinkedAccounts(
        settings.loggedInAccounts,
      );
      settings.lastLoggedInIdByGame = updatedLastLoggedInIdByGame;
      settings.loggedInAccounts = updatedLoggedInAccounts;

      await mainRefs.didRender;

      settings[isPersistent] = MAX_TIME;
      writeState.settings = settings;
    })();

  return initSettingsPromise;
}

export function setOverlayPosition(overlayId, game, x, y, isDragged) {
  writeSettings([game, "overlays", "positions", overlayId], {
    x,
    y,
    isDragged,
  });
}

export function setOverlayPositions(overlayPositions, game) {
  for (const overlayId of Object.keys(overlayPositions)) {
    const { x, y, isDragged } = overlayPositions[overlayId];
    setOverlayPosition(overlayId, game, x, y, isDragged);
  }
}

export function setupRefetchListeners(event) {
  if (!event) return;
  handleMessage(event, () => {
    const {
      route: { currentPath, searchParams, state },
    } = router;
    searchParams.set("refetch", "true");
    updateRoute(currentPath, searchParams, state);
  });
}

export function teardownRefetchListeners(event) {
  if (!event) return;
  handleMessage(event, () => {});
}

async function assignFavoriteGames(settings) {
  await readData(["library", "lastInstalledGames"]);

  const userInstalledGames = await getInstalledGames();

  const {
    library: { lastInstalledGames },
  } = readState;

  // In unusual cases, settings may contain invalid values (?)
  let {
    settings: { favoriteGames },
  } = readState;
  if (!Array.isArray(favoriteGames)) favoriteGames = [];

  // if the user has no installed games, and no favorite games, then
  // add all games to favorite games (this could re-init their favorite games
  // if they removed all favorite games, but the app looks bad without games)
  if (!userInstalledGames.length && !favoriteGames.length) {
    settings.favoriteGames = ACTIVE_GAMES;
    return;
  }

  // get new games since last detection
  const newInstalledGames = userInstalledGames.filter(
    (game) => !lastInstalledGames.includes(game),
  );

  // nothing new, don't change favorite games
  if (!newInstalledGames.length) return;

  // append new games to favorites, no duplicates
  settings.favoriteGames = [
    ...new Set([...favoriteGames, ...newInstalledGames]),
  ];

  // and update our last installed games list
  const allInstalledGames = [
    ...new Set([...lastInstalledGames, ...newInstalledGames]),
  ];
  allInstalledGames[isPersistent] = MAX_TIME;
  writeState.library.lastInstalledGames = allInstalledGames;
}

// CS2 is incredibly cursed that I have to write shit like this to sanitize the steam ids
const RegExpSteamIdRename = /steam\d+/;
function sanitizeSteamId(steamId: string) {
  return steamId.replace(RegExpSteamIdRename, (i) => {
    const numbers = i.match(/\d+/)[0];
    return `STEAM_${numbers}`;
  });
}
/**
 * In-place delete of steam accounts with an invalid steam id
 */
function removeInvalidSteamAccounts(target) {
  const cs2 = target?.cs2,
    csgo = target?.csgo;
  // lastLoggedInIdByGame
  if (typeof cs2 === "string") target.cs2 = sanitizeSteamId(cs2);
  if (typeof csgo === "string") target.csgo = sanitizeSteamId(csgo);
  // loggedInAccounts
  if (typeof cs2 === "object") renameCsAccounts(cs2);
  if (typeof csgo === "object") renameCsAccounts(csgo);
}
// This removes invalid steam accounts, renames them properly and removes duplicates
function renameCsAccounts(steamAccounts) {
  if (!steamAccounts) return;
  const accounts = [];
  // For some reason the key does not match the steamId property, so we use the steamId property > steamId key
  for (const steamId in steamAccounts) {
    const account = steamAccounts[steamId];
    if (!account) continue;
    accounts.push([account.steamId, account.username]); // Removes duplicate entries with the same STEAM_ID
    delete steamAccounts[steamId]; // Purge useless or duplicate STEAM_ID
  }
  const entries = Object.fromEntries(accounts);
  for (const steamId in entries) {
    steamAccounts[steamId] = {
      steamId,
      username: entries[steamId],
    };
  }
}

export function migrateLinkedAccounts(object) {
  removeInvalidSteamAccounts(object);

  const gamesToMigrate = [
    GAME_SHORT_NAMES[GAME_SYMBOL_LOL],
    GAME_SHORT_NAMES[GAME_SYMBOL_TFT],
  ];

  const invalidRegions = new Set([
    "null",
    "undefined",
    "sg",
    "ph",
    "id1",
    "vn",
    "th",
    "tw",
  ]);

  for (const game of gamesToMigrate) {
    const accountsObject = object?.[game];
    if (!accountsObject) continue;

    if (typeof accountsObject === "string" && accountsObject.includes(":")) {
      // lastLoggedInIdByGame: just remove legacy identifier and let it be re-set with PUUID
      object[game] = null;
    } else if (typeof accountsObject === "object") {
      // loggedInAccounts: save by PUUID if it's valid
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const [key, summoner] of Object.entries<any>(accountsObject)) {
        if (!key.includes(":")) continue;

        if (summoner && summoner.puuid && !invalidRegions.has(summoner.region))
          accountsObject[summoner.puuid] = summoner;

        delete accountsObject[key];
      }
    }
  }

  return object;
}

export function updateRiotRegionAction(region) {
  writeSettings(["riotRegion"], region);
}

const commitMap: Partial<VolatileState> = {};

export function setVolatileKV<K extends keyof VolatileState>(
  key: K,
  value: VolatileState[K],
): Promise<void> {
  if (typeof key !== "string")
    throw new Error(`Key "${key}" must be a string.`);

  if (value && typeof value === "object") {
    value[isVolatile] = true;

    const currentValue = readState.volatile[key];
    if (
      currentValue &&
      typeof currentValue === "object" &&
      deepEqual(value, currentValue)
    ) {
      return Promise.resolve();
    }
  }

  return new Promise<void>((resolve) => {
    // This defers actually writing until the very end. The reason why is because
    // React will run hooks twice to ensure no bad side effects are happening in hooks.
    // For example, if a useEffect has a return function and this function is called
    // from that hook, it will write 3 times.
    //
    // This effectively ensures that a write will only happen once per tick.
    if (!(key in commitMap)) {
      queueMicrotask(() => {
        writeState.volatile[key] = commitMap[key];
        delete commitMap[key];
        resolve();
      });
    }

    commitMap[key] = value;
  });
}

export function showEphemeralNotification(notification) {
  setVolatileKV("ephemeralNotification", {
    severity: "minor",
    ...notification,
    isEphemeral: true,
  });
}

export function hideEphemeralNotification() {
  setVolatileKV("ephemeralNotification", null);
}

export function addDismissedNotification(notificationId) {
  writeSettings(
    ["dismissedNotifications"],
    [...readState.settings.dismissedNotifications, notificationId],
  );
}

export function deleteDismissedNotification(notificationId) {
  const dismissedNotifications = [
    ...readState.settings.dismissedNotifications,
  ].filter((id) => id !== notificationId);
  writeSettings(["dismissedNotifications"], dismissedNotifications);
}

export function setLiveGame(
  game?: GameSymbol | null | undefined,
  data?: object | null | undefined,
  timestamp: number = 0,
) {
  if (game && typeof game !== "symbol") {
    devWarn("Game must be a symbol");
    return;
  }

  if (!data) {
    setVolatileKV("liveGame", {});
    return;
  }

  setVolatileKV("liveGame", {
    game,
    timestamp,
    ...data,
  });
}

export function resetLiveGame(game?: GameSymbol | undefined) {
  setLiveGame(game, null, 0);
}

export function setFeatureFlag(
  flag: keyof FeatureFlags,
  isEnabled: boolean,
): Promise<void> {
  writeState.features[flag] = isEnabled;
  return featurePromises[flag];
}

// reducer
function reduceToSymbolList(gamenameBoolMap) {
  return Object.entries(gamenameBoolMap).reduce((arr, [shortName, value]) => {
    if (value) arr.push(GAME_SYMBOL_BY_SHORT_NAME[shortName]);
    return arr;
  }, []);
}

export async function getInstalledGames() {
  const installedGames = await blitzMessage(EVENTS.GET_INSTALLED_GAMES);
  return installedGames ? reduceToSymbolList(installedGames) : [];
}

export async function getRunningGames() {
  const runningGames = await blitzMessage(EVENTS.GET_RUNNING_GAMES);
  const list = runningGames ? reduceToSymbolList(runningGames) : [];
  if (lolRefs.lolClient.isConnected) {
    list.push(GAME_SYMBOL_LOL);
    list.push(GAME_SYMBOL_TFT);
  }
  return list;
}

export function addFavoriteGame(gameSymbol) {
  if (readState.settings.favoriteGames.includes(gameSymbol)) return;

  writeSettings(
    ["favoriteGames"],
    [...readState.settings.favoriteGames, gameSymbol],
  );
}

export function removeFavoriteGame(gameSymbol) {
  const favoriteGames = [...readState.settings.favoriteGames].filter(
    (symbol) => symbol !== gameSymbol,
  );
  writeSettings(["favoriteGames"], favoriteGames);
}

export function swapFavoriteGames(indexA: number, indexB: number) {
  const games = [...readState.settings.favoriteGames];
  const active = games.filter((g) =>
    (ACTIVE_GAMES as GameSymbol[]).includes(g),
  );
  const inactive = games.filter(
    (g) => !(ACTIVE_GAMES as GameSymbol[]).includes(g),
  );

  [active[indexA], active[indexB]] = [active[indexB], active[indexA]] as const;
  const favoriteGames = active.concat(inactive);
  writeSettings(["favoriteGames"], favoriteGames);
}

// When writing one key-value in settings, it is strongly recommended to use this.
export function writeSettings<const P extends readonly PropertyKey[]>(
  path: P,
  value?: DigAppState<["settings", ...P]>,
) {
  const settings = clone(readState.settings || freshInitialState.settings);
  const [writePath, writeKey] = digPath(path, settings);
  if (value === undefined) delete writePath[writeKey];
  else writePath[writeKey] = value;
  settings[isPersistent] = MAX_TIME;
  writeState.settings = settings;
}

// REFACTOR: this exists mainly because there hasn't been any
// design consideration what the non-game specific navigation should
// look like. Once that is figured out, this should be removed.
export function writeLocalState(path, value) {
  const localState = clone(readState.localState);
  const [writePath, writeKey] = digPath(path, localState);
  writePath[writeKey] = value;
  localState[isPersistent] = MAX_TIME;
  writeState.localState = localState;
}

export function setLocalProfileId(gameSymbol, accountKey) {
  //just like loggedInAccounts, lastLoggedInIdByGame , only save for lol
  let gameShortName = gameShortNameReassign(GAME_SHORT_NAMES[gameSymbol]);
  if (gameShortName === "tft") gameShortName = "lol";

  writeLocalState(["currentProfileId", gameShortName], accountKey);
}

export function enableAppDraggable() {
  if (!IS_APP) return;
  writeState.volatile.disableAppDrag = false;
}

export function disableAppDraggable() {
  if (!IS_APP) return;
  writeState.volatile.disableAppDrag = true;
}

export function setCMPBannerVisible(val: boolean) {
  writeState.volatile.isCMPBannerVisible = val;
}

let snackbarId = 0;

export function showSnackbar(message: SnackbarMessageInit) {
  const { priority } = message;

  const snackbar = clone(readState.volatile.snackbar) ?? [];

  const msg: SnackbarMessage = {
    id: !message.id ? snackbarId++ : undefined,
    ...message,
  };

  if (typeof message.id === "string") {
    const idx = snackbar.findIndex((queued) => queued.id === msg.id);

    // If the ID already existed, remove it
    if (idx !== -1) {
      snackbar.splice(idx, 1);
    }
  }

  // If the priority is low, insert at the back of the queue
  if (priority === "low" && snackbar.length > 0) {
    if (typeof msg.id === "string") {
      devDebug(`Adding snackbar "${msg.id}" to the bottom of the queue`);
    } else {
      devDebug(`Adding snackbar to the bottom of the queue`);
    }

    snackbar.splice(0, 0, msg);
  } else {
    snackbar.push(msg);
  }

  // We don't use setVolatileKV here because we want access to the new
  // snackbar array immediately, in case multiple get queued up

  snackbar[isVolatile] = true;

  writeState.volatile["snackbar"] = ref(snackbar);
}

/**
 * Remove a snackbar message by ID.
 *
 * You should make sure your use case isn't already covered by an option within `SnackbarMessageInit`.
 */
export function removeSnackbarMessage(
  id: string | number,
): SnackbarMessage | null {
  const snackbar = clone(readState.volatile.snackbar) ?? [];

  const idx = snackbar.findIndex((message) => message.id === id);

  if (idx === -1) return null;

  const [message] = snackbar.splice(idx, 1);

  snackbar[isVolatile] = true;

  writeState.volatile["snackbar"] = ref(snackbar);

  return message;
}

export async function overrideFeatureFlagsFromCMS() {
  // At some point this was restricted to app-only to increase performance,
  // but should not be necessary as this does not run on the server and
  // is considered a side effect promise to the default route.
  if (IS_NODE) {
    // in ignored case, treat current feature flags as final
    appRefs.fetchedFeatureFlags.resolve();
    return;
  }

  allowRead();
  const features = readState.features;
  const { featureFlags: cmsFlags } = await fetchFeatureFlags();

  const MACHINE_ID = (await getEnvironmentIdentifier())?.id;

  if (!MACHINE_ID) {
    if (!IS_MAC) devError("Unable to get machine ID");
    return;
  }

  const modifiedFlags = {}; // store this to avoid reading the later set values

  for (const flag of cmsFlags) {
    // MAYBE TEMPORARY FOR NOW? neuter ads feature flags from CMS
    // because we currently need to toggle an entire set of flags.
    if (/ads/i.test(flag.feature)) continue;

    if (!(flag && flag.feature in features)) {
      devLog("Feature does not exist", flag);
      continue;
    }

    if (
      !(
        typeof flag.feature === "string" &&
        Number.isFinite(flag.percentage) &&
        Number.isInteger(flag.version)
      )
    ) {
      devError("Invalid flag", flag);
      continue;
    }

    const salt = flag.feature;
    const fingerprint = getFeatureIdentifier(MACHINE_ID, salt) % 100;
    const enabled = fingerprint < flag.percentage;
    // If the feature is already enabled, don't override it
    if (!enabled) continue;
    writeState.features[flag.feature] = modifiedFlags[flag.feature] = enabled;
  }

  waitForFeatureFlags(modifiedFlags).then(appRefs.fetchedFeatureFlags.resolve);
  eventBus.emit(EVENT_FEATURE_VERSION, {
    machineId: MACHINE_ID,
    ...getABFeatureValue(),
  });
}

export function registerDismissSnackbarsOnRouteChange() {
  function snackbarIsDismissable(snackbar: SnackbarDismissedByRouting) {
    if (typeof snackbar.dismissOnRouteChange === "function") {
      return snackbar.dismissOnRouteChange(router.route);
    }

    return snackbar.dismissOnRouteChange === true;
  }

  function removeSnackbars() {
    const snackbars = clone(readState.volatile.snackbar) ?? [];
    snackbars.filter(snackbarIsDismissable).forEach((snackbar) => {
      removeSnackbarMessage(snackbar.id);
    });
  }

  router.events.on(EVENT_CHANGE_ROUTE, removeSnackbars);

  return () => router.events.off(EVENT_CHANGE_ROUTE, removeSnackbars);
}

/**
 * Library Notifications - displayed on the secondary nav library icon
 */

type LibraryNotification = {
  title: Translation;
  description: Translation;
  thumbnail: string;
};

declare module "@/__main__/app-state.mjs" {
  interface VolatileState {
    libraryNotification?: LibraryNotification;
  }
}

export function setLibraryNotification(notification: LibraryNotification) {
  setVolatileKV("libraryNotification", notification);
}

class PublicError extends Error {
  isPublic: boolean;
  internalClass: string;
}

type LogLevel = "warning" | "error";
export function showRouteErrorNotification(error: Error, level?: LogLevel);
export function showRouteErrorNotification(message: string, level?: LogLevel);

export function showRouteErrorNotification(error: unknown, level?: LogLevel) {
  const publicError =
    error instanceof Error
      ? (error as PublicError)
      : new PublicError(error as string);
  publicError.isPublic = true;
  publicError.internalClass = level;
  router.error = publicError;
  router.events.emit(router.events.EVENT_LOADED_DATA);
}

export function addNewNavIconKeys(newKeys: string[]) {
  const newNavItems = clone(readState.marketing.newNavItems);
  for (const key of newKeys) {
    if (!newNavItems.hasOwnProperty(key)) newNavItems[key] = true;
  }
  newNavItems[isPersistent] = MAX_TIME;

  writeState.marketing.newNavItems = newNavItems;
}

export function setNotifyNewNavItem(key: string, value: boolean) {
  const newNavItems = clone(readState.marketing.newNavItems);
  newNavItems[key] = value;
  newNavItems[isPersistent] = MAX_TIME;

  writeState.marketing.newNavItems = newNavItems;
}

export function setShowFeedbackModal(value: boolean) {
  writeState.volatile["feedbackModalOpen"] = value;
}

export function loadPreviousMatchTileAd() {
  return readData(["marketing", "previousMatchTileAd"], {
    initializeOnce: true,
  });
}

export function setPreviousMatchTileAd(gameSymbol, matchId) {
  const previousMatchTileAd = {
    ...readState.marketing.previousMatchTileAd,
    [gameSymbol]: matchId,
  };

  previousMatchTileAd[isPersistent] = Date.now() + 1000 * 60 * 60 * 24 * 7;
  writeState.marketing.previousMatchTileAd = previousMatchTileAd;
}

export function loadSeenGamePromoFeatures() {
  return readData(["marketing", "seenPromoFeatures"], { initializeOnce: true });
}

export function markGamePromoFeaturesAsSeen(features: string[]) {
  if (!features.length) return;

  const seenPromoFeatures = clone(readState.marketing.seenPromoFeatures);

  seenPromoFeatures[isPersistent] = MAX_TIME;
  for (const feature of features) seenPromoFeatures[feature] = true;

  writeState.marketing.seenPromoFeatures = seenPromoFeatures;
}
